import {useMemo, useEffect} from 'react'
import {useHeaderContext} from '../../../contexts/header-context'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    HEADER_GRADIENT_MAP,
    HEADER_COMPOSITING_LAYER,
    HEADER_ICON_FILTER,
    HEADER_DROP_SHADOW
} from '../config'
import useEffectiveHeaderVariant from './useEffectiveHeaderVariant'

/**
 * Hook that encapsulates all transparent header variant logic:
 * hover detection, visual state computation, gradient, drop shadow,
 * logo/icon color filters. The variant in effect (CMS vs. context defaults)
 * is resolved by useEffectiveHeaderVariant.
 */
const useTransparentHeader = (styles) => {
    const {headerRef, isScrolled, headerHoverZone, setHeaderHoverZone} = useHeaderContext()
    const {
        gradient: effectiveGradient,
        dropShadow: effectiveDropShadow,
        isTransparent,
        isWhiteVariant
    } = useEffectiveHeaderVariant()

    const isDesktop = useBreakpointValue({base: false, lg: true})

    const isHeaderHovered = headerHoverZone !== 'none'
    const shouldBeTransparent = isTransparent && !isScrolled && !isHeaderHovered

    // Reliable hover detection via document-level pointermove.
    // onMouseEnter/onMouseLeave misses cases: modal overlays (Portals),
    // navigation re-renders, and overlay DOM removal.
    // Also considers open megamenu popovers (rendered as portals outside header DOM).
    useEffect(() => {
        if (!isTransparent || !isDesktop) return

        let inside = false

        const onPointerMove = (e) => {
            const headerEl = headerRef.current
            if (!headerEl) return

            const inHeader = headerEl.contains(e.target)
            const inPopover = !!e.target.closest?.('[data-testid="popover-menu"]')
            const nowInside = inHeader || inPopover

            if (nowInside !== inside) {
                inside = nowInside
                setHeaderHoverZone(nowInside ? 'header' : 'none')
            }
        }

        document.addEventListener('pointermove', onPointerMove, {passive: true})
        return () => document.removeEventListener('pointermove', onPointerMove)
    }, [isTransparent, isDesktop, setHeaderHoverZone])

    // Container style based on variant + scroll + hover
    const getContainerVariantStyle = () => {
        if (!isTransparent) return {}
        if (isHeaderHovered) {
            return {
                ...styles.containerTransparent,
                ...styles.containerTransparentHovered,
                // Force own compositing layer so the overlay's backdrop-filter
                // cannot bleed through the header background
                ...HEADER_COMPOSITING_LAYER
            }
        }
        if (isScrolled) {
            return {
                ...styles.containerTransparent,
                ...styles.containerTransparentScrolled
            }
        }
        return styles.containerTransparent
    }

    // Text/icon color: white only for V1 in transparent default state
    const textColor = shouldBeTransparent && isWhiteVariant ? 'white' : undefined

    // Logo CSS filter
    const logoFilterStyle = useMemo(() => {
        if (!shouldBeTransparent) return HEADER_ICON_FILTER.default
        if (isWhiteVariant) return HEADER_ICON_FILTER.whiteVariant
        return HEADER_ICON_FILTER.darkVariant
    }, [shouldBeTransparent, isWhiteVariant])

    // CMS icon color filter
    const cmsIconColorFilter = useMemo(() => {
        if (!shouldBeTransparent) return undefined
        if (isWhiteVariant) return HEADER_ICON_FILTER.whiteVariant
        return HEADER_ICON_FILTER.darkVariant
    }, [shouldBeTransparent, isWhiteVariant])

    // Gradient background
    const gradientBackground = useMemo(() => {
        if (!shouldBeTransparent || effectiveGradient === 'none') return null
        return HEADER_GRADIENT_MAP[effectiveGradient] || null
    }, [shouldBeTransparent, effectiveGradient])

    // Drop shadow style for text/logo readability
    const dropShadowStyle = shouldBeTransparent && effectiveDropShadow ? HEADER_DROP_SHADOW : {}

    return {
        isTransparent,
        getContainerVariantStyle,
        textColor,
        logoFilterStyle,
        cmsIconColorFilter,
        gradientBackground,
        dropShadowStyle
    }
}

export default useTransparentHeader
